/* eslint no-shadow: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import AuditHistory from 'components/modules/shipments/AuditHistory';
import DataPoller from 'components/ui/DataPoller';
import { withTranslation } from 'react-i18next';
import CustomerStore from 'stores/CustomerSearchStore';
import CustomerActionCreators from 'actions/CustomersActionCreators';
import LabelValue from 'components/ui/tailwind/label-value';
import AccountContacts from 'components/v2/contacts';
import CargomaticCard from 'components/v2/card';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import EditButton from '../../../v2/buttons/EditButton';
import Badge from '../../../v2/Badge';
import { Avatar } from '../Avatar';

const POLLING_FREQ = 10000;

const REQUIRED_ATTRIBUTES = [
  'first_name',
  'last_name',
  'username',
  'phone',
  'email',
];
const getRequiredAsterisk = (field) => {
  return REQUIRED_ATTRIBUTES.includes(field) ? '*' : '';
};

class VendorDetails extends Component {
  static propTypes = {
    details: PropTypes.object,
    onSaveDetails: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      showAccountModal: false,
      category: null,
      vendor: props.details,
      audits: [],
      errors: {
        first_name: {},
        last_name: {},
        username: {},
        phone: {},
        email: {},
      },
      changed: {},
    };
  }

  componentDidMount() {
    CustomerStore.addAuditChangeListener(this.onAuditChange);
  }

  componentWillUnmount() {
    CustomerStore.removeAuditChangeListener(this.onAuditChange);
  }

  isRequiredField = () => `${this.props.t('IS_REQUIRED_FIELD')}`;

  onAuditChange = () => {
    const audits = CustomerStore.getCustomerAudits();
    if (!_.isEqual(audits, this.state.audits)) {
      return this.setState({ audits });
    }
  };

  errorsExist() {
    const { errors } = this.state;
    let errorsExist = false;
    _.map(errors, (value, key) => {
      if (!_.isEmpty(errors[key])) errorsExist = true;
    });
    return errorsExist;
  }

  onSave = () => {
    const { changed, vendor } = this.state;

    _.map(changed, (item, key) => {
      if (!['badges', 'test'].includes(key)) {
        if (vendor[key] && vendor[key] instanceof String) {
          vendor[key] = vendor[key].trim();
        }
      }
    });
    const updates = _.pick(vendor, Object.keys(changed));
    updates._id = vendor._id; // Didn't change but needed for the PATCH

    this.props.onSaveDetails('Vendor', updates);
  };

  getUserAudits = () => {
    CustomerActionCreators.fetchCustomerAudits(this.props.details._id);
  };

  handleFieldChange = (field, value) => {
    const { vendor, changed, errors } = this.state;
    vendor[field] = value;
    changed[field] = true;
    const { t } = this.props;

    errors[field] =
      !value && REQUIRED_ATTRIBUTES.includes(field)
        ? { message: `${t(field.toUpperCase())} ${this.isRequiredField()}` }
        : {};

    this.setState({ vendor, changed, errors });
  };

  renderAccountModal = () => {
    const { errors, vendor } = this.state;
    const { first_name, last_name, username, phone, email } = vendor;

    const { category } = this.state;
    const { t } = this.props;

    return (
      <div>
        {category === 'account' && (
          <div className="grid sm:grid-cols-6 sm:gap-4 gap-y-6">
            <CargomaticInput
              className="sm:col-span-3"
              error={errors.first_name}
              label={`${t('FIRST_NAME')} ${getRequiredAsterisk('first_name')}`}
              info={t('VENDOR_FIRST_NAME')}
              onChange={(value) => this.handleFieldChange('first_name', value)}
              value={first_name}
            />
            <CargomaticInput
              className="sm:col-span-3"
              error={errors.last_name}
              label={`${t('LAST_NAME')} ${getRequiredAsterisk('last_name')}`}
              info={t('VENDOR_LAST_NAME')}
              onChange={(value) => this.handleFieldChange('last_name', value)}
              value={last_name}
            />
            <CargomaticInput
              className="sm:col-span-3"
              error={errors.phone}
              label={`${t('PHONE_NUMBER')} ${getRequiredAsterisk('phone')}`}
              info={t('VENDOR_PHONE_NUMBER')}
              onChange={(value) => this.handleFieldChange('phone', value)}
              value={phone}
            />
            <CargomaticInput
              className="sm:col-span-4"
              error={errors.email}
              label={`${t('EMAIL_ADDRESS')} ${getRequiredAsterisk('email')}`}
              info={t('VENDOR_EMAIL')}
              onChange={(value) => this.handleFieldChange('email', value)}
              value={email}
              maxLength={254}
            />
          </div>
        )}
        <div className="mt-4 form-actions">
          <button
            className="button button-green"
            onClick={this.onSave}
            disabled={this.errorsExist()}
          >
            {t('SAVE')}
          </button>
        </div>
      </div>
    );
  };

  render() {
    const { vendor } = this.state;
    const { t } = this.props;

    return (
      <div className="mx-auto my-16 max-w-screen-2xl md:my-32 lg:my-16">
        <DataPoller action={this.getUserAudits} frequency={POLLING_FREQ} />
        <div className="lg:grid lg:grid-cols-4 lg:gap-6">
          <div className="col-span-1">
            <CargomaticCard className="mb-6">
              <div className="text-center">
                <div className="flex justify-center py-6">
                  <Avatar
                    key={vendor._id}
                    userId={vendor._id}
                    className="h-32 w-32 cursor-pointer rounded-full"
                  />
                </div>
                <Badge color="blue">{t('VENDOR', { count: 1 })}</Badge>
                <div className="flex justify-center py-6">
                  <LabelValue
                    value={`${vendor.first_name} ${vendor.last_name}`}
                  />
                </div>
              </div>
            </CargomaticCard>
            <CargomaticCard
              className="mb-6"
              header={() => (
                <header
                  onClick={() => {
                    this.setState({
                      category: 'account',
                      showAccountModal: true,
                    });
                  }}
                  className="flex items-center justify-between"
                >
                  <h3 className="my-0">{t('ACCOUNT_DETAILS')}</h3>
                  <EditButton />
                </header>
              )}
            >
              <LabelValue
                value={`${vendor.first_name} ${vendor.last_name}`}
                icon="user"
              />
              <LabelValue value={vendor.phone} icon="phone" />
              <LabelValue value={vendor.email} icon="envelope" />
            </CargomaticCard>
            <div className="mt-6">
              <button
                className="w-full button button-hollow-red"
                onClick={this.props.onChangePasswordClick}
              >
                {t('CHANGE_PASSWORD')}
              </button>
            </div>
          </div>
          <div className="col-span-2">
            <CargomaticCard
              className="mb-6"
              header={() => <h3 className="my-0">{t('AUDIT_HISTORY')}</h3>}
            >
              <AuditHistory audits={this.state.audits} paginate />
            </CargomaticCard>
            <CargomaticCard>
              <AccountContacts contacts={vendor.contacts} userId={vendor._id} />
            </CargomaticCard>
          </div>
        </div>
        {this.state.showAccountModal && (
          <CargomaticModal
            show
            onHide={() => this.setState({ showAccountModal: false })}
            title={t('ACCOUNT_INFORMATION')}
            body={this.renderAccountModal()}
            footer={null}
            location="middle"
          />
        )}
      </div>
    );
  }
}

export default withTranslation()(VendorDetails);
